/**
 * AB#650 — a blueprint tile's icon: a colour thumbnail ("mini page") painted with
 * that theme's OWN palette, read from its `static/style.css` `:root` block.
 *
 * Both palettes (light + dark) are always rendered, stacked, and toggled by CSS
 * only: the admin's current mode picks one (`dark:` = `.dark` on <html>), and
 * hover/focus-visible on the tile flips to the other. No JS mode detection, no
 * hover state, so an OS-level scheme change needs no re-render.
 */

import { LayoutTemplate, Star } from 'lucide-react';
import { useMemo } from 'react';
import { parseRootVars, isColorVar, toHex6, type CssVar, type PaletteMode } from '../themeVars';
// ponytail: relative, not `@/lib/utils` — vitest.config.ts has no path alias and
// isn't ours to edit, and this module's pure part (readPalette) is unit-tested.
import { cn } from '../../lib/utils';
import { styleFilePath, useSourceFile, type Blueprint } from './blueprint';

export interface Palette {
  bg: string;
  text: string;
  accent: string;
  muted: string;
  border: string;
}

/**
 * Candidate var names per slot, best first. Bundled themes use `--color-*`;
 * the shadcn-ish names are there so a hand-rolled blueprint still paints.
 */
const SLOTS: Record<keyof Palette, string[]> = {
  bg: ['--color-bg', '--color-background', '--background', '--color-body-bg'],
  text: ['--color-text', '--color-foreground', '--color-fg', '--foreground'],
  accent: ['--color-link', '--color-primary', '--color-accent', '--primary', '--accent'],
  muted: ['--color-text-muted', '--color-muted', '--color-secondary', '--muted-foreground'],
  border: ['--color-border', '--border'],
};

/** A value we can hand to `style` as-is. `var(...)` can't resolve in our DOM. */
function usableColor(value: string | undefined): string | null {
  if (!value) return null;
  const v = value.trim();
  if (!v || v.includes('var(') || v.includes('gradient')) return null;
  return toHex6(v) ?? v;
}

/**
 * Merge one palette's `:root` vars over the light ones — a dark block usually
 * only overrides the colours it changes. Returns null when neither exists.
 */
function varsFor(css: string, mode: PaletteMode): CssVar[] | null {
  const light = parseRootVars(css, 'light');
  if (mode === 'light') return light?.vars ?? null;
  const dark = parseRootVars(css, 'dark');
  if (!dark) return light?.vars ?? null; // light-only theme: same swatch both ways
  return [...(light?.vars ?? []), ...dark.vars];
}

export function readPalette(css: string, mode: PaletteMode): Palette | null {
  const vars = varsFor(css, mode);
  if (!vars) return null;

  // Later wins (dark overrides light).
  const byName = new Map(vars.map((v) => [v.name, v.value]));
  const pick = (names: string[]) => {
    for (const n of names) {
      const c = usableColor(byName.get(n));
      if (c) return c;
    }
    return null;
  };

  const bg = pick(SLOTS.bg);
  const text = pick(SLOTS.text);
  if (!bg || !text) return null; // not a palette we can paint — caller shows the glyph

  // Unconventional var names: fall back to the first colour-ish var that isn't
  // already spoken for, so an off-pattern theme still gets an accent block.
  const spare = vars
    .filter((v) => isColorVar(v.name))
    .map((v) => usableColor(v.value))
    .find((c) => c && c !== bg && c !== text);

  return {
    bg,
    text,
    accent: pick(SLOTS.accent) ?? spare ?? text,
    muted: pick(SLOTS.muted) ?? text,
    border: pick(SLOTS.border) ?? text,
  };
}

/** The painted page: header rule, heading, two muted lines, an accent button. */
function MiniPage({ p, className }: { p: Palette; className: string }) {
  return (
    <div
      className={cn('absolute inset-0 flex flex-col gap-[3px] p-[6px] transition-opacity', className)}
      style={{ background: p.bg }}
      aria-hidden
    >
      <div className="h-[5px] w-2/3 rounded-[1px]" style={{ background: p.text }} />
      <div className="h-px w-full" style={{ background: p.border }} />
      <div className="mt-[2px] h-[3px] w-full rounded-[1px]" style={{ background: p.muted }} />
      <div className="h-[3px] w-4/5 rounded-[1px]" style={{ background: p.muted }} />
      <div className="mt-auto h-[9px] w-1/2 rounded-[2px]" style={{ background: p.accent }} />
    </div>
  );
}

/** Light layer on top in light mode; hover/focus swaps the two. */
const LIGHT_LAYER =
  'opacity-100 dark:opacity-0 group-hover/tile:opacity-0 group-focus-visible/tile:opacity-0 dark:group-hover/tile:opacity-100 dark:group-focus-visible/tile:opacity-100';
const DARK_LAYER =
  'opacity-0 dark:opacity-100 group-hover/tile:opacity-100 group-focus-visible/tile:opacity-100 dark:group-hover/tile:opacity-0 dark:group-focus-visible/tile:opacity-0';

export function BlueprintSwatch({
  blueprint,
  selected,
  isDefault,
  size = 'md',
}: {
  blueprint: Blueprint;
  /** Only tints the fallback glyph — the selection ring lives on the tile box. */
  selected: boolean;
  /** `settings.account_theme_id` points here — starred, independently of selection. */
  isDefault: boolean;
  /** `sm` = the 24px list-row swatch. */
  size?: 'md' | 'sm';
}) {
  const small = size === 'sm';
  // react-query dedupes/caches this per blueprint; a handful of tiles at most.
  const path = styleFilePath(blueprint);
  const css = useSourceFile(blueprint.source, path).data?.data?.content;

  const palettes = useMemo(() => {
    if (!css) return null;
    const light = readPalette(css, 'light');
    const dark = readPalette(css, 'dark');
    return light && dark ? { light, dark } : null;
  }, [css]);

  return (
    <div
      className={cn(
        'relative shrink-0 overflow-hidden rounded-md border border-border/60',
        small ? 'size-6' : 'size-16',
      )}
    >
      {isDefault && (
        // ponytail: span carries the tooltip — a `title` ATTRIBUTE on an <svg>
        // isn't a reliable tooltip across browsers, an SVG needs a <title> child.
        // Outside the scaled box below, or it would shrink with the artwork.
        <span
          className="absolute top-0.5 left-0.5 z-10 rounded-full bg-background/80 p-px leading-none"
          title="Site default"
        >
          <Star
            className={cn('fill-primary text-primary', small ? 'size-2' : 'size-3')}
            aria-label="Site default"
          />
        </span>
      )}
      {/* ponytail: the artwork is drawn once at 64px and scaled down for the list
          row (24/64 = 0.375) — one set of proportions to keep honest, not two. */}
      <div className={cn('absolute top-0 left-0 size-16', small && 'origin-top-left scale-[0.375]')}>
        {palettes ? (
          <>
            <MiniPage p={palettes.light} className={LIGHT_LAYER} />
            <MiniPage p={palettes.dark} className={DARK_LAYER} />
          </>
        ) : (
          // No stylesheet, or no :root palette to read (Tailwind-compiled themes).
          <div className="flex h-full items-center justify-center bg-muted/40">
            <LayoutTemplate
              className={cn('size-7', selected ? 'text-primary' : 'text-muted-foreground')}
              aria-hidden
            />
          </div>
        )}
      </div>
    </div>
  );
}
